import {
  type DeliveryData,
  GeoModal,
  type LocationData
} from '@carvana/merch-geolocation';
import React, { useContext, useMemo, useRef } from 'react';
import Modal from '@/common/Modal';
import { axios } from '~/clients';
import { DELIVERY_CONTEXT } from '~/constants/endpoints';
import UserContext, { useUserLocation } from '~/context/common/UserContext';
import getGeoLocationApi from '~/services/common/getGeoLocationApi';

type GeolocationConfig = React.ComponentProps<typeof GeoModal>['config'];

const fetchDelivery = (zipcode: string): Promise<DeliveryData> =>
  axios
    .get<DeliveryData>(`${DELIVERY_CONTEXT}?zip5=${zipcode}`)
    .then(res => res.data);

type ConfigArgs = {
  setGeolocationModalOpen: (open: boolean) => void;
};

function useGeoLocationConfig({
  setGeolocationModalOpen
}: ConfigArgs): GeolocationConfig {
  const {
    location: { setData: setLocation }
  } = useContext(UserContext);
  const userLocation = useUserLocation();
  const geolocation = useRef<Partial<LocationData>>(userLocation);

  return useMemo<GeolocationConfig>(
    () => ({
      fetchDelivery,
      geoApi: getGeoLocationApi(),
      onCloseHandler() {
        setGeolocationModalOpen(false);
      },
      onUserLocationChange({ location }) {
        if (geolocation.current.zip === location.zip) return;

        setLocation(location);
      }
    }),
    [setGeolocationModalOpen, setLocation]
  );
}

type OwnProps = {
  isGeolocationModalOpen: boolean;
  setGeolocationModalOpen: (open: boolean) => void;
};

export default function GeolocationModal({
  isGeolocationModalOpen,
  setGeolocationModalOpen
}: OwnProps) {
  const config = useGeoLocationConfig({ setGeolocationModalOpen });

  return (
    <GeoModal config={config} isOpen={isGeolocationModalOpen}>
      {({ content }) => (
        <Modal
          open={isGeolocationModalOpen}
          onToggle={() => {
            setGeolocationModalOpen(false);
          }}
          headerText="Update Your Location"
        >
          {content}
        </Modal>
      )}
    </GeoModal>
  );
}
